/**
 * Offline outbox. Every write is appended here first (IndexedDB), applied optimistically to the
 * cache, then replayed FIFO against the idempotent API. iOS has no Background Sync, so flushes are
 * triggered by the page (launch, visibilitychange, online, manual, after each write) — see sync.ts.
 * This module has no DOM dependencies so the service worker can flush too (Background Sync on
 * browsers that support it).
 */
import { api, apiFetch, ApiError, OfflineError, SessionExpiredError } from "./api";
import { db, deleteBlobs, getBlob, guardQuota, kv, type OutboxRecord } from "./idb";
import { isLegacyOp, opDomain, type LegacyOp, type Op } from "./ops";
import { localDate } from "./insights";
import { createStore } from "./store";

export type SyncState = {
  pending: number;
  failed: number;
  syncing: boolean;
  lastSyncAt: number | null;
  lastError: "offline" | "session" | "server" | null;
};

export const syncStore = createStore<SyncState>({
  pending: 0,
  failed: 0,
  syncing: false,
  lastSyncAt: null,
  lastError: null,
});

const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("peakstamps-sync") : null;
export type SyncMessage = { type: "flushed"; domains: string[] } | { type: "enqueued" };

/** The signed-in account's id, read from IndexedDB so the service worker sees it too (see account.ts). */
async function currentUserId(): Promise<string | null> {
  return (await kv.get<{ id: string }>("identity"))?.id ?? null;
}

export async function enqueue(op: Op): Promise<void> {
  const userId = (await currentUserId()) ?? undefined;
  await guardQuota(async () => {
    const d = await db();
    const rec: OutboxRecord = { op, createdAt: Date.now(), attempts: 0, state: "pending", userId };
    await d.add("outbox", rec);
  });
  await refreshCounts();
  channel?.postMessage({ type: "enqueued" } satisfies SyncMessage);
}

export async function outboxRecords(): Promise<OutboxRecord[]> {
  return (await db()).getAll("outbox");
}

/** Ops not yet confirmed by the server (pending + failed), in order — used to overlay fetched data. */
export async function unsyncedOps(): Promise<Op[]> {
  return (await outboxRecords()).map((r) => r.op).filter((op): op is Op => !isLegacyOp(op));
}

export async function refreshCounts() {
  const recs = await outboxRecords();
  syncStore.set((s) => ({
    ...s,
    pending: recs.filter((r) => r.state === "pending").length,
    failed: recs.filter((r) => r.state === "failed").length,
  }));
}

async function perform(op: Op): Promise<void> {
  switch (op.type) {
    case "ascent.put":
      return void (await api.put(`/api/ascents/${op.id}`, op.data));
    case "ascent.delete":
      return api.del(`/api/ascents/${op.id}`);
    case "gpx.put": {
      const blob = await getBlob(op.blobKey);
      if (!blob) throw new Error("missing_blob");
      await apiFetch(`/api/ascents/${op.ascentId}/gpx`, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": "application/gpx+xml" },
      });
      return;
    }
    case "gpx.delete":
      return api.del(`/api/ascents/${op.ascentId}/gpx`);
    case "ascentPhoto.put": {
      const blob = await getBlob(op.blobKey);
      if (!blob) throw new Error("missing_blob");
      await apiFetch(`/api/ascents/${op.ascentId}/photos/${op.photoId}`, { method: "PUT", body: blob, headers: { "Content-Type": blob.type } });
      return;
    }
    case "ascentPhoto.delete":
      return api.del(`/api/ascents/${op.ascentId}/photos/${op.photoId}`);
    case "list.put":
      return void (await api.put(`/api/lists/${op.id}`, op.data));
    case "list.delete":
      return api.del(`/api/lists/${op.id}`);
    case "list.reorder":
      return void (await api.post(`/api/lists/reorder`, { ids: op.ids }));
    case "listItem.put":
      return void (await api.put(`/api/lists/${op.listId}/items/${op.peakId}`, op.data));
    case "listItem.delete":
      return api.del(`/api/lists/${op.listId}/items/${op.peakId}`);
    case "listItems.add":
      return void (await api.post(`/api/lists/${op.listId}/items`, { peakIds: op.peakIds }));
    case "peak.put":
      return void (await api.put(`/api/peaks/${op.id}`, op.data));
    case "pins.set":
      return void (await api.put(`/api/pins`, { peakIds: op.peakIds }));
    case "avatar.put": {
      const blob = await getBlob(op.blobKey);
      if (!blob) throw new Error("missing_blob");
      await apiFetch(`/api/profile/avatar/${op.avatarId}`, { method: "PUT", body: blob, headers: { "Content-Type": blob.type } });
      return;
    }
    case "avatar.delete":
      return api.del(`/api/profile/avatar`);
    case "profile.put":
      return void (await api.put(`/api/profile`, op.data));
    case "social.put":
      return void (await api.put(`/api/social/me`, op.data));
    case "follow.put":
      return void (await api.put(`/api/follows/${op.person.id}`, {}));
    case "follow.delete":
      return api.del(`/api/follows/${op.userId}`);
    case "follower.approve":
      return void (await api.put(`/api/followers/${op.person.id}`, {}));
    case "follower.remove":
      return api.del(`/api/followers/${op.userId}`);
    case "block.put":
      return void (await api.put(`/api/blocks/${op.person.id}`, {}));
    case "block.delete":
      return api.del(`/api/blocks/${op.userId}`);
    case "report.put":
      return void (await api.put(`/api/reports/${op.id}`, op.data));
    case "notices.read":
      return void (await api.put(`/api/notices/read`, { until: op.until }));
    case "pause.put":
      return void (await api.put(`/api/social/pause/${op.week}?today=${localDate(new Date())}`, {}));
    case "pause.delete":
      return api.del(`/api/social/pause/${op.week}?today=${localDate(new Date())}`);
    case "piedra.put":
      return void (await api.put(`/api/people/${op.ownerId}/ascents/${op.ascentId}/piedra`, {}));
    case "piedra.delete":
      return api.del(`/api/people/${op.ownerId}/ascents/${op.ascentId}/piedra`);
    case "comment.put":
      return void (await api.put(`/api/people/${op.ownerId}/ascents/${op.ascentId}/comments/${op.id}`, { body: op.body }));
    case "comment.delete":
      return api.del(`/api/people/${op.ownerId}/ascents/${op.ascentId}/comments/${op.id}`);
    case "challenge.join":
      return void (await api.put(`/api/challenges/${encodeURIComponent(op.id)}?today=${localDate(new Date())}`, {}));
    case "challenge.leave":
      return api.del(`/api/challenges/${encodeURIComponent(op.id)}`);
    case "push.prefs":
      return void (await api.put(`/api/push/prefs`, { mute: op.mute }));
    case "invite.put":
      return void (await api.put(`/api/groups/${op.ascentId}/invites/${op.person.id}`, {}));
    case "invite.delete":
      return api.del(`/api/groups/${op.ascentId}/invites/${op.userId}`);
    case "invite.accept":
      return void (await api.put(`/api/invites/${op.invite.owner.id}/${op.invite.ascentId}`, { id: op.id }));
    case "invite.decline":
      return api.del(`/api/invites/${op.ownerId}/${op.ascentId}`);
    default:
      // Every op has a case: one without would count as sent and be dropped.
      return op satisfies never;
  }
}

/** IndexedDB blobs an op owns (a legacy photo.put also left an image and its thumbnail). */
function blobKeys(op: Op | LegacyOp): string[] {
  if (op.type === "gpx.put" || op.type === "avatar.put" || op.type === "ascentPhoto.put") return [op.blobKey];
  if (op.type === "photo.put") return [op.blobKey, op.thumbBlobKey];
  return [];
}

/**
 * 404 on these means the target is gone (an ascent deleted meanwhile) or no longer visible (a profile
 * hidden, a block): nothing left to do.
 */
const DROP_ON_404 = new Set<Op["type"]>([
  "gpx.put",
  "gpx.delete",
  "ascentPhoto.put",
  "ascentPhoto.delete",
  "listItem.put",
  "listItem.delete",
  "listItems.add",
  "ascent.delete",
  "list.delete",
  "follow.put",
  "follower.approve",
  "block.put",
  "report.put",
  "piedra.put",
  "piedra.delete",
  "comment.put",
  "comment.delete",
  "challenge.join",
  // An ascent gone (or withdrawn), someone no longer visible: nothing left to invite or answer.
  "invite.put",
  "invite.delete",
  "invite.accept",
  "invite.decline",
]);

/**
 * Lifting a pause whose week went by while it waited offline: that week's pause is history, nothing left
 * to do. A pause that could not be set stays as a failed change, so it is not lost without a word.
 */
const pastPause = (op: Op, e: ApiError) => op.type === "pause.delete" && e.status === 422 && e.body?.error === "pause_past";

async function withLock<T>(fn: () => Promise<T>): Promise<T | null> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (!locks) return fn();
  return locks.request("peakstamps-outbox", { ifAvailable: true }, async (lock) => (lock ? fn() : null));
}

export type FlushResult = { synced: number; domains: Set<string>; stoppedBy: SyncState["lastError"] };

/**
 * Replay the outbox in order. Stops at the first transient failure; permanent 4xx go to "failed".
 * Only the signed-in account's records are sent (and records from before accounts, which it adopts).
 */
export async function flush(): Promise<FlushResult | null> {
  return withLock(async () => {
    const userId = await currentUserId();
    if (!userId) return null;
    const d = await db();
    const all = (await d.getAll("outbox")).filter((r) => !r.userId || r.userId === userId);
    // Legacy ops (removed photo uploads) have no endpoint any more: done, whatever their state.
    for (const rec of all) {
      if (!isLegacyOp(rec.op)) continue;
      await d.delete("outbox", rec.seq!);
      await deleteBlobs(blobKeys(rec.op));
    }
    const recs = all.filter((r): r is OutboxRecord & { op: Op } => r.state === "pending" && !isLegacyOp(r.op));
    const result: FlushResult = { synced: 0, domains: new Set(), stoppedBy: null };
    if (!recs.length) {
      syncStore.set((s) => ({ ...s, lastError: null, lastSyncAt: Date.now() }));
      await refreshCounts();
      return result;
    }
    syncStore.set((s) => ({ ...s, syncing: true }));
    try {
      for (const rec of recs) {
        try {
          await perform(rec.op);
          await d.delete("outbox", rec.seq!);
          const keys = blobKeys(rec.op);
          if (keys.length) await deleteBlobs(keys);
          result.synced++;
          result.domains.add(opDomain(rec.op));
        } catch (e) {
          if (e instanceof OfflineError) {
            result.stoppedBy = "offline";
            break;
          }
          if (e instanceof SessionExpiredError) {
            result.stoppedBy = "session";
            break;
          }
          // The server switched social off: keep the social change for when it is back, go on with the rest.
          if (e instanceof ApiError && e.status === 404 && e.body?.error === "social_off") continue;
          if (e instanceof ApiError && ((e.status === 404 && DROP_ON_404.has(rec.op.type)) || pastPause(rec.op, e))) {
            await d.delete("outbox", rec.seq!);
            await deleteBlobs(blobKeys(rec.op));
            result.domains.add(opDomain(rec.op));
            continue;
          }
          if (e instanceof ApiError && (e.status >= 500 || e.status === 429)) {
            await d.put("outbox", { ...rec, attempts: rec.attempts + 1, lastError: e.message });
            result.stoppedBy = "server";
            break;
          }
          // Permanent (validation, conflict, missing local blob…): park it so the rest can go through.
          await d.put("outbox", {
            ...rec,
            attempts: rec.attempts + 1,
            state: "failed",
            lastError: e instanceof ApiError ? `${e.status} ${e.message}` : String((e as Error)?.message ?? e),
          });
          result.domains.add(opDomain(rec.op));
        }
      }
    } finally {
      syncStore.set((s) => ({
        ...s,
        syncing: false,
        lastError: result.stoppedBy,
        lastSyncAt: result.stoppedBy ? s.lastSyncAt : Date.now(),
      }));
      await refreshCounts();
    }
    if (result.domains.size) {
      channel?.postMessage({ type: "flushed", domains: [...result.domains] } satisfies SyncMessage);
    }
    return result;
  });
}

export async function retryFailed() {
  const d = await db();
  for (const r of await d.getAll("outbox")) if (r.state === "failed") await d.put("outbox", { ...r, state: "pending" });
  await refreshCounts();
}

export async function discardFailed() {
  const d = await db();
  for (const r of await d.getAll("outbox")) {
    if (r.state !== "failed") continue;
    await d.delete("outbox", r.seq!);
    await deleteBlobs(blobKeys(r.op));
  }
  await refreshCounts();
}

export function onSyncMessage(fn: (m: SyncMessage) => void): () => void {
  if (!channel) return () => {};
  const h = (e: MessageEvent<SyncMessage>) => fn(e.data);
  channel.addEventListener("message", h);
  return () => channel.removeEventListener("message", h);
}
